import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, GripVertical, Check, AlertCircle } from 'lucide-react';

/**
 * Composant générique pour configurer des listes d'éléments (statuts, catégories, actions, etc.)
 * @param {Object} props
 * @param {string} props.title - Titre de la section
 * @param {string} props.description - Description
 * @param {Object} props.api - API object avec getAll, create, update, delete, reorder
 * @param {Array} props.fields - Liste des champs à afficher [{key, label, type, options}]
 * @param {string} props.colorField - Nom du champ couleur (optionnel)
 * @param {string} props.iconField - Nom du champ icône (optionnel)
 */
const WidgetGenericConfig = ({ 
  title, 
  description, 
  api, 
  fields = [],
  colorField = 'couleur',
  iconField = 'icone',
  codeField = 'code',
  labelField = 'label'
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const EMOJI_OPTIONS = ['📋', '📝', '✅', '❌', '⏳', '🔄', '📞', '📧', '📮', '📅', '💰', '🏠', '👤', 'ℹ️', '⚠️', '📌', '📤', '🆕', '💼', '🎯', '📊', '🔔'];
  const COLOR_OPTIONS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#9ca3af', '#059669', '#7c3aed'];
  const BG_COLOR_OPTIONS = [
    { value: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Bleu' },
    { value: 'bg-green-100 text-green-800 border-green-200', label: 'Vert' },
    { value: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Orange' },
    { value: 'bg-red-100 text-red-800 border-red-200', label: 'Rouge' },
    { value: 'bg-purple-100 text-purple-800 border-purple-200', label: 'Violet' },
    { value: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Gris' },
    { value: 'bg-cyan-100 text-cyan-800 border-cyan-200', label: 'Cyan' },
    { value: 'bg-pink-100 text-pink-800 border-pink-200', label: 'Rose' },
  ];
  const TAILWIND_BG_OPTIONS = [
    { value: 'bg-gray-400', label: 'Gris' },
    { value: 'bg-blue-500', label: 'Bleu' },
    { value: 'bg-blue-600', label: 'Bleu foncé' },
    { value: 'bg-green-500', label: 'Vert' },
    { value: 'bg-green-600', label: 'Vert foncé' },
    { value: 'bg-orange-500', label: 'Orange' },
    { value: 'bg-purple-500', label: 'Violet' },
    { value: 'bg-purple-600', label: 'Violet foncé' },
    { value: 'bg-red-500', label: 'Rouge' },
    { value: 'bg-amber-600', label: 'Ambre' },
  ];

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const res = await api.getAllWithInactive ? await api.getAllWithInactive() : await api.getAll();
      setItems(res.data || []);
      setError(null);
    } catch (err) {
      console.error('Erreur chargement:', err);
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (id, data) => {
    setSaving(true);
    try {
      await api.update(id, data);
      await loadItems();
      setEditingId(null);
      setEditData({});
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!newItem[codeField] || !newItem[labelField]) {
      setError('Le code et le libellé sont obligatoires');
      return;
    }
    setSaving(true);
    try {
      await api.create(newItem);
      await loadItems();
      setIsAdding(false);
      setNewItem({});
      setError(null);
    } catch (err) {
      console.error('Erreur création:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet élément ?')) return;
    try {
      await api.delete(id);
      await loadItems();
    } catch (err) {
      console.error('Erreur suppression:', err);
      setError('Erreur lors de la suppression');
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditData({ ...item });
  };

  const renderField = (field, value, onChange, isNew = false) => {
    const fieldValue = value || '';
    
    if (field.type === 'emoji') {
      return (
        <div className="flex flex-wrap gap-1">
          {EMOJI_OPTIONS.map(emoji => (
            <button
              key={emoji}
              onClick={() => onChange(emoji)}
              className={`w-8 h-8 flex items-center justify-center rounded border text-lg ${
                fieldValue === emoji ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {emoji}
            </button>
          ))}
        </div>
      );
    }
    
    if (field.type === 'color') {
      return (
        <div className="flex flex-wrap gap-1">
          {COLOR_OPTIONS.map(color => (
            <button
              key={color}
              onClick={() => onChange(color)}
              className={`w-8 h-8 rounded border-2 ${
                fieldValue === color ? 'border-gray-800 ring-2 ring-offset-1 ring-gray-400' : 'border-transparent'
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      );
    }

    if (field.type === 'bgColor') {
      return (
        <div className="flex flex-wrap gap-1">
          {BG_COLOR_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className={`px-2 py-1 rounded text-xs ${opt.value} ${
                fieldValue === opt.value ? 'ring-2 ring-offset-1 ring-gray-400' : ''
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );
    }

    if (field.type === 'tailwindBg') {
      return (
        <div className="flex flex-wrap gap-1">
          {TAILWIND_BG_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className={`px-2 py-1 rounded text-xs text-white ${opt.value} ${
                fieldValue === opt.value ? 'ring-2 ring-offset-1 ring-gray-400' : ''
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );
    }
    
    if (field.type === 'number') {
      return (
        <input
          type="number"
          value={fieldValue}
          onChange={(e) => onChange(parseInt(e.target.value) || 0)}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
        />
      );
    }
    
    return (
      <input
        type="text"
        value={fieldValue}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        placeholder={field.placeholder || field.label}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {description && <p className="text-sm text-gray-500">{description}</p>}
        </div>
        <button
          onClick={() => { setIsAdding(true); setNewItem({}); }}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          disabled={isAdding}
        >
          <Plus className="w-4 h-4" />
          Ajouter
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Liste */}
      <div className="space-y-2">
        {/* Formulaire d'ajout */}
        {isAdding && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Code *</label>
                <input
                  type="text"
                  value={newItem[codeField] || ''}
                  onChange={(e) => setNewItem({ ...newItem, [codeField]: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  placeholder="code_unique"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Libellé *</label>
                <input
                  type="text"
                  value={newItem[labelField] || ''}
                  onChange={(e) => setNewItem({ ...newItem, [labelField]: e.target.value })}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  placeholder="Libellé affiché"
                />
              </div>
            </div>
            {fields.map(field => (
              <div key={field.key}>
                <label className="block text-xs font-medium text-gray-700 mb-1">{field.label}</label>
                {renderField(field, newItem[field.key], (val) => setNewItem({ ...newItem, [field.key]: val }), true)}
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setIsAdding(false); setNewItem({}); setError(null); }}
                className="px-3 py-1.5 text-gray-600 hover:text-gray-800 text-sm"
              >
                Annuler
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
              >
                {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Check className="w-4 h-4" />}
                Créer
              </button>
            </div>
          </div>
        )}

        {/* Liste des items */}
        {items.map((item) => (
          <div
            key={item.id}
            className={`bg-white border rounded-lg p-4 ${
              editingId === item.id ? 'border-blue-300 ring-1 ring-blue-200' : 'border-gray-200'
            } ${!item.actif ? 'opacity-50' : ''}`}
          >
            {editingId === item.id ? (
              // Mode édition
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Code</label>
                    <input
                      type="text"
                      value={editData[codeField] || ''}
                      disabled
                      className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Libellé</label>
                    <input
                      type="text"
                      value={editData[labelField] || ''}
                      onChange={(e) => setEditData({ ...editData, [labelField]: e.target.value })}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
                {fields.map(field => (
                  <div key={field.key}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{field.label}</label>
                    {renderField(field, editData[field.key], (val) => setEditData({ ...editData, [field.key]: val }))}
                  </div>
                ))}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={() => { setEditingId(null); setEditData({}); }}
                    className="px-3 py-1.5 text-gray-600 hover:text-gray-800 text-sm"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => handleSave(item.id, editData)}
                    disabled={saving}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
                  >
                    {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Save className="w-4 h-4" />}
                    Sauvegarder
                  </button>
                </div>
              </div>
            ) : (
              // Mode affichage
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" />
                  {item[iconField] && <span className="text-xl">{item[iconField]}</span>}
                  {item[colorField] && !item[colorField].startsWith('bg-') && (
                    <div 
                      className="w-4 h-4 rounded-full border border-gray-200" 
                      style={{ backgroundColor: item[colorField] }}
                    />
                  )}
                  {item[colorField] && item[colorField].startsWith('bg-') && (
                    <span className={`px-2 py-0.5 rounded text-xs ${item[colorField]}`}>
                      {item[labelField]}
                    </span>
                  )}
                  <div>
                    <span className="font-medium text-gray-900">{item[labelField]}</span>
                    <span className="ml-2 text-xs text-gray-400">({item[codeField]})</span>
                    {item.progress !== undefined && (
                      <span className="ml-2 text-xs text-gray-500">{item.progress}%</span>
                    )}
                    {item.description && (
                      <p className="text-xs text-gray-500">{item.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => startEdit(item)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {items.length === 0 && !isAdding && (
          <div className="text-center py-8 text-gray-500">
            Aucun élément configuré
          </div>
        )}
      </div>
    </div>
  );
};

export default WidgetGenericConfig;
